import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "./Login.css";
import { getLockRemaining, useAuth } from "./AuthContext";
import { getBusiness } from "../common/settingsStore";
import { DEFAULT_BUSINESS_LOGO } from "../common/defaultLogo";

const DEMO_MODE = process.env.REACT_APP_DEMO_MODE === "true";
const DEMO_EMAIL = process.env.REACT_APP_DEMO_EMAIL || "";
const DEMO_PASSWORD = process.env.REACT_APP_DEMO_PASSWORD || "";

const FEATURES = [
  { icon: "bi-receipt", text: "Create and print invoices in seconds" },
  { icon: "bi-cash-coin", text: "Track paid, partial and due amounts" },
  { icon: "bi-file-earmark-pdf", text: "Branded PDF with your logo and details" },
];

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const business = getBusiness();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(""); // "form" | "demo" | ""
  const [lockSeconds, setLockSeconds] = useState(Math.ceil(getLockRemaining() / 1000));

  useEffect(() => {
    if (lockSeconds <= 0) return;
    const t = setTimeout(() => setLockSeconds(Math.ceil(getLockRemaining() / 1000)), 1000);
    return () => clearTimeout(t);
  }, [lockSeconds]);

  const from = location.state?.from?.pathname || "/";
  if (isAuthenticated) return <Navigate to={from} replace />;

  const doLogin = async (mail, pass, keep, source) => {
    setError("");
    setBusy(source);
    try {
      const result = await login(mail, pass, keep);
      if (result.ok) {
        toast.success(source === "demo" ? "Welcome to the demo!" : "Welcome back!");
        navigate(from, { replace: true });
      } else {
        setError(result.error);
        setLockSeconds(Math.ceil(getLockRemaining() / 1000));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    doLogin(email, password, remember, "form");
  };

  // Demo credentials stay out of the UI; one click signs in.
  const loginDemo = () => doLogin(DEMO_EMAIL, DEMO_PASSWORD, false, "demo");

  const locked = lockSeconds > 0;

  return (
    <div className="login-page">
      <aside className="login-brand">
        <div className="login-brand-inner">
          <img
            src={business.logo || DEFAULT_BUSINESS_LOGO}
            alt=""
            className="login-brand-logo"
          />
          <h1 className="login-brand-name">{business.name}</h1>
          {business.services && <p className="login-brand-tagline">{business.services}</p>}
          <ul className="login-features">
            {FEATURES.map((f) => (
              <li key={f.icon}>
                <i className={`bi ${f.icon}`} aria-hidden="true" />
                {f.text}
              </li>
            ))}
          </ul>
        </div>
      </aside>

      <main className="login-main">
        <div className="login-card">
          <img
            src={business.logo || DEFAULT_BUSINESS_LOGO}
            alt=""
            className="login-card-logo d-lg-none"
          />
          <h2 className="login-title">Sign in</h2>
          <p className="login-subtitle">Welcome back. Enter your details to continue.</p>

          {DEMO_MODE && DEMO_EMAIL && (
            <>
              <div className="login-demo">
                <div className="login-demo-icon" aria-hidden="true">
                  <i className="bi bi-stars" />
                </div>
                <div className="login-demo-text">
                  <div className="login-demo-title">Just looking around?</div>
                  <div className="login-demo-sub">Try the demo with one click. No email or password needed.</div>
                </div>
                <button
                  type="button"
                  className="btn login-demo-btn"
                  onClick={loginDemo}
                  disabled={!!busy || locked}
                >
                  {busy === "demo" ? (
                    <span className="spinner-border spinner-border-sm me-2" />
                  ) : (
                    <i className="bi bi-play-circle-fill me-2" />
                  )}
                  Try Demo Login
                </button>
              </div>
              <div className="login-divider">
                <span>or sign in with your account</span>
              </div>
            </>
          )}

          <form onSubmit={handleLogin} noValidate>
            <div className="mb-3">
              <label htmlFor="loginemail" className="form-label">Email</label>
              <div className="login-input">
                <i className="bi bi-envelope" aria-hidden="true" />
                <input
                  id="loginemail"
                  type="email"
                  className="form-control"
                  placeholder="name@example.com"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="mb-2">
              <label htmlFor="loginpassword" className="form-label">Password</label>
              <div className="login-input">
                <i className="bi bi-lock" aria-hidden="true" />
                <input
                  id="loginpassword"
                  type={showPassword ? "text" : "password"}
                  className="form-control"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyUp={(e) => setCapsLock(e.getModifierState?.("CapsLock"))}
                />
                <button
                  type="button"
                  className="login-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`} />
                </button>
              </div>
              {capsLock && (
                <div className="login-hint">
                  <i className="bi bi-capslock" /> Caps Lock is on
                </div>
              )}
            </div>

            <div className="form-check mb-3">
              <input
                id="remember"
                type="checkbox"
                className="form-check-input"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <label htmlFor="remember" className="form-check-label">
                Keep me signed in for 30 days
              </label>
            </div>

            <div aria-live="polite">
              {(error || locked) && (
                <div className="login-error">
                  <i className="bi bi-exclamation-circle" />
                  {locked ? `Too many attempts. Try again in ${lockSeconds}s.` : error}
                </div>
              )}
            </div>

            <button
              className="btn btn-primary w-100 login-submit"
              type="submit"
              disabled={!!busy || locked || !email || !password}
            >
              {busy === "form" ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : (
                <i className="bi bi-box-arrow-in-right me-2" />
              )}
              Sign in
            </button>
          </form>

        </div>
        <p className="login-footer">© {new Date().getFullYear()} {business.name}</p>
      </main>
    </div>
  );
};

export default Login;
